import React from "react";
import { flexRender } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import type { Table as TTable } from "~/shared/react-table/app-table";
import {
  TableBody,
  TableHead,
  TableHeader,
  Table,
  TableRow,
} from "~/components/table";

import { PaginationController } from "~/components/pagination-controller/pagination-controller";
import { BooksTableHeader } from "./books-table-header";
import { BooksTableBody, type BooksTableRowData } from "./books-table-body";
import { BooksTableNoResults } from "./books-table-no-results";

export const BooksTable = <TData extends BooksTableRowData>({
  table,
  isLoading,
  hasPagination,
  onBookClick,
  defaultPageSize,
  query,
  hasDetails,
  hasNoResultsBanner,
  actions,
  header,
}: {
  table: TTable<TData>;
  isLoading: boolean;
  hasPagination?: boolean;
  onBookClick: (product: TData, rowIndex: number) => void;
  defaultPageSize?: number;
  query?: string;
  hasDetails?: boolean;
  hasNoResultsBanner?: boolean;
  actions?: React.ReactNode;
  header?: React.ReactNode;
}) => {
  const { t } = useTranslation(["components"]);

  const count = table.getRowCount();

  const selectedCount = table.getSelectedCount();
  const isSelectedAll = table.getSelectedAll();

  const selectAllButtonLabel = isSelectedAll
    ? t("components:booksTable.clearSelection")
    : t("components:booksTable.selectAll", { count });

  const noResults = hasNoResultsBanner && count === 0 && !isLoading;

  return (
    <div className="space-y-4 text-left">
      <BooksTableHeader
        selectedCount={selectedCount}
        isSelectedAll={isSelectedAll}
        actions={actions}
        header={header}
      />

      {noResults ? (
        <BooksTableNoResults query={query} />
      ) : (
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((tableHeader) => (
                  <TableHead key={tableHeader.id} colSpan={tableHeader.colSpan}>
                    {tableHeader.isPlaceholder
                      ? null
                      : flexRender(
                          tableHeader.column.columnDef.header,
                          tableHeader.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody data-testid="books-table-body">
            <BooksTableBody
              onRowClick={onBookClick}
              isLoading={isLoading}
              table={table}
              hasDetails={hasDetails}
            />
          </TableBody>
        </Table>
      )}

      {hasPagination ? (
        <PaginationController
          itemsCount={count}
          onSelectAll={() => table.toggleSelectAll()}
          pageSizeLabel={t("components:booksTable.pageSize")}
          selectAllLabel={count ? selectAllButtonLabel : undefined}
          defaultPageSize={defaultPageSize}
        />
      ) : null}
    </div>
  );
};

BooksTable.displayName = "BooksTable";
